/**
 * Review-question frameworks other than PICO.
 *
 * - **SPIDER** — Sample, Phenomenon of Interest, Design, Evaluation, Research type.
 *   Cooke A, Smith D, Booth A. Beyond PICO: the SPIDER tool for qualitative evidence
 *   synthesis. Qualitative Health Research 2012;22(10):1435–43.
 * - **SPICE** — Setting, Perspective, Intervention (or phenomenon of Interest),
 *   Comparison, Evaluation. Booth A. Clear and present questions: formulating questions
 *   for evidence based practice. Library Hi Tech 2006;24(3):355–68.
 * - **PEO** — Population, Exposure, Outcome: questions about exposures and experiences,
 *   not interventions; used in the JBI Manual for Evidence Synthesis for association
 *   (aetiology) reviews.
 * - **PCC** — Population, Concept, Context: the framework for scoping reviews in the JBI
 *   Manual for Evidence Synthesis.
 * - **ECLIPSE** — Expectation, Client group, Location, Impact, Professionals, SErvice.
 *   Wildridge V, Bell L. How CLIP became ECLIPSE: a mnemonic to assist in searching for
 *   health policy/management information. Health Information and Libraries Journal
 *   2002;19(2):113–5.
 *
 * PICO has its own tool. A framework here only organises the question and the search
 * terms; it does not decide which one suits the review.
 */

import { t } from "@/lib/i18n/interpolate";
import {
  conceptsToQuery,
  genericQuery,
} from "@/lib/tool-kit/research-workflow/boolean";

const RE_SPACE_BEFORE_PUNCT = /\s+([?,.])/g;
const RE_SPACE_RUN = /\s{2,}/g;

export type FrameworkId = "spider" | "spice" | "peo" | "pcc" | "eclipse";

export interface Framework {
  id: FrameworkId;
  /** Element keys, in the order of the acronym. */
  keys: readonly string[];
  /** Elements whose words go into the question only when filled in, as a clause. */
  optional: readonly string[];
  /** Elements searched by default; the rest tend to narrow a first search too far. */
  searched: readonly string[];
}

export const FRAMEWORKS: readonly Framework[] = [
  {
    id: "spider",
    keys: ["s", "pi", "d", "e", "r"],
    optional: [],
    searched: ["s", "pi"],
  },
  {
    id: "spice",
    keys: ["s", "p", "i", "c", "e"],
    optional: ["c"],
    searched: ["s", "p", "i"],
  },
  {
    id: "peo",
    keys: ["p", "e", "o"],
    optional: [],
    searched: ["p", "e", "o"],
  },
  {
    id: "pcc",
    keys: ["p", "concept", "context"],
    optional: ["context"],
    searched: ["p", "concept"],
  },
  {
    id: "eclipse",
    keys: ["e", "c", "l", "i", "p", "se"],
    optional: ["i"],
    searched: ["c", "se"],
  },
];

export function frameworkFor(id: FrameworkId): Framework {
  return FRAMEWORKS.find((framework) => framework.id === id) ?? FRAMEWORKS[0];
}

export interface ElementInput {
  /** The element as it should read in the question. */
  value: string;
  /** Search terms for it, one per line. */
  terms: string;
  include: boolean;
}

export type FrameworkInput = Readonly<Record<string, ElementInput>>;

export function emptyInput(framework: Framework): FrameworkInput {
  return Object.fromEntries(
    framework.keys.map((key) => [
      key,
      { value: "", terms: "", include: framework.searched.includes(key) },
    ])
  );
}

function textOf(input: FrameworkInput, key: string): string {
  return input[key]?.value.trim() ?? "";
}

/** The words an element contributes to the search: its terms box, or else its text. */
export function elementTerms(input: FrameworkInput, key: string): string {
  const own = input[key]?.terms.trim() ?? "";
  return own === "" ? textOf(input, key) : own;
}

/** The elements that are switched on for the search and have something to search. */
export function searchKeys(
  framework: Framework,
  input: FrameworkInput
): string[] {
  return framework.keys.filter(
    (key) => input[key]?.include && elementTerms(input, key) !== ""
  );
}

export function searchString(
  framework: Framework,
  input: FrameworkInput
): string {
  return genericQuery(
    searchKeys(framework, input).map((key) => elementTerms(input, key))
  );
}

export function builderQuery(
  framework: Framework,
  input: FrameworkInput
): string {
  return conceptsToQuery(
    searchKeys(framework, input).map((key) => elementTerms(input, key))
  );
}

export interface QuestionPhrases {
  /** The question with `{key}` for every element. */
  question: string;
  /** For each optional element, the clause that replaces `{key}` when it is filled in. */
  clauses: Readonly<Record<string, string>>;
  /** The bracketed stand-in for an element left empty. */
  missing: Readonly<Record<string, string>>;
}

/** The draft question in the reader's words; an empty element shows as its bracketed name. */
export function buildQuestion(
  framework: Framework,
  input: FrameworkInput,
  phrases: QuestionPhrases
): string {
  const values: Record<string, string> = {};
  for (const key of framework.keys) {
    const value = textOf(input, key);
    if (framework.optional.includes(key)) {
      values[key] =
        value === "" ? "" : t(phrases.clauses[key] ?? "{value}", { value });
    } else {
      values[key] = value === "" ? (phrases.missing[key] ?? `[${key}]`) : value;
    }
  }
  return t(phrases.question, values)
    .replace(RE_SPACE_BEFORE_PUNCT, "$1")
    .replace(RE_SPACE_RUN, " ");
}
